import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import AuthCard from "./AuthCard";
import { resetPassword } from "../../shared/api";
import { getResetPasswordErrorKey, validateResetPasswordForm } from "./helpers";

function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [values, setValues] = useState({
    newPassword: "",
    confirmNewPassword: "",
  });
  const [errorKey, setErrorKey] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorKey("");
    setIsSuccess(false);

    const validationErrorKey = validateResetPasswordForm({
      token,
      newPassword: values.newPassword,
      confirmNewPassword: values.confirmNewPassword,
    });
    if (validationErrorKey) {
      setErrorKey(validationErrorKey);
      return;
    }

    setIsSubmitting(true);
    try {
      await resetPassword({ token, newPassword: values.newPassword });
      setIsSuccess(true);
      setValues({ newPassword: "", confirmNewPassword: "" });
      window.setTimeout(() => {
        navigate("/sign-in", { replace: true });
      }, 1500);
    } catch (error) {
      setErrorKey(getResetPasswordErrorKey(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthCard
      title={t("auth.resetPasswordTitle")}
      subtitle={t("auth.resetPasswordDescription")}
    >
      <Stack component="form" spacing={2} onSubmit={handleSubmit}>
        {!token ? (
          <Alert severity="error">{t("auth.errors.resetPassword.invalidToken")}</Alert>
        ) : null}

        <TextField
          required
          type={showNewPassword ? "text" : "password"}
          label={t("auth.newPasswordLabel")}
          helperText={t("auth.passwordMinLength")}
          value={values.newPassword}
          onChange={(event) =>
            setValues((prev) => ({ ...prev, newPassword: event.target.value }))
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={t("auth.togglePasswordVisibility")}
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  edge="end"
                >
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          required
          type={showConfirmNewPassword ? "text" : "password"}
          label={t("auth.confirmNewPasswordLabel")}
          value={values.confirmNewPassword}
          onChange={(event) =>
            setValues((prev) => ({
              ...prev,
              confirmNewPassword: event.target.value,
            }))
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={t("auth.togglePasswordVisibility")}
                  onClick={() => setShowConfirmNewPassword((prev) => !prev)}
                  edge="end"
                >
                  {showConfirmNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {errorKey ? <Typography color="error">{t(errorKey)}</Typography> : null}

        {isSuccess ? (
          <Alert severity="success">{t("auth.resetPasswordSuccess")}</Alert>
        ) : null}

        <Button type="submit" variant="contained" disabled={isSubmitting || !token}>
          {isSubmitting ? t("common.loading") : t("auth.resetPasswordButton")}
        </Button>
        <Button component={RouterLink} to="/sign-in">
          {t("auth.goToSignIn")}
        </Button>
      </Stack>
    </AuthCard>
  );
}

export default ResetPasswordPage;
